import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import cx from "classnames";
import { Hooks, Util } from "@xt/xtbase";
import store from "store";
import { Dropdown, MenuProps, Modal, message, Checkbox, Tooltip } from "antd";
import { post_cfd_openPosition, get_cfd_positions, post_cfd_calcQuantity, post_cfd_calcMargin } from "api/v4/cfd";
import { build_prop_open_position_body, post_prop_open_position } from "api/v4/proptrading";
import Storage from "utils/storage";
import useCoinMemo from "@/components/Pages/Trade/_hook/useCoinMemo";
import useCfdConfig from "../../_hook/useCfdConfig";
import useAxiosCancelFun from "hooks/useAxiosCancelFun";
import { isPropTradingCfdTradeRoute } from "@/utils/cfd/isPropTradingCfdTradeRoute";
import AppInputNumber from "@/components/App/input/number";
import XtInputNumber, { XtInputNumberRefProps } from "@/components/Xt/Input/number";
import XtSvg from "@/components/Xt/Svg";
import Slider from "@/components/Pages/Trade/_cmpt/form/_cmpt/slider";
import DepositDrawer from "../depositDrawer";
import DepositOrConvertDrawer from "../depositOrConvertDrawer";
import LeverageSelector from "../leverageSelector";
import { isFiatMarketCode } from "@/utils/fiatMarket";
import styles from "./index.module.scss";
import addIcon from "@/assets/img/add.png";

const { useTranslation } = Hooks;
const { getUrl, Big } = Util;

enum TradeTypeEnum {
  limit = "limit", // 限价单
  market = "market", // 市价单
}

enum TradeSideEnum {
  buy = "buy", // 买涨
  sell = "sell", // 买跌
}

// 下单类型枚举
enum OrderInputTypeEnum {
  quantity = "quantity", // 按币数量
  value = "value", // 按开仓价值
  margin = "margin", // 按保证金金额
}

interface Props {
  onOpenPositionSuccess?: () => void;
  usePropOpenApi?: boolean;
  /** 仅展示：可用余额行币种后缀（如 Prop 页 USDX）；不传则用交易对计价币 */
  availableBalanceDisplayUnit?: string;
}

const TradeFormPro: React.FC<Props> = ({
  onOpenPositionSuccess,
  usePropOpenApi = false,
  availableBalanceDisplayUnit,
}) => {
  const router = useRouter();
  const t = useTranslation();
  const { name } = store.market;
  const { isLogin, authInfo } = store.user;
  const { coinQuantityUpperCase, coinPriceUpperCase, coinQuantityPrecisionMarket, coinPricePrecisionMarket, coinPricePrecisionCurrency } = useCoinMemo();
  const { tradeRecentOnce } = store.trade;
  const cfdConfig = useCfdConfig();

  // 获取完整的可用余额（不取整）；Prop 页由 useUserLogin 将 USDX 资产写入 currencyPrice
  const availableBalance = useMemo(() => {
    if (!store.balances?.currencyPrice?.availableAmount) return "0";
    return store.balances.currencyPrice.availableAmount;
  }, [store.balances?.currencyPrice?.availableAmount]);

  const availableBalanceLabel = useMemo(() => {
    if (!availableBalance) return "0";
    // 和现货保持一致，使用 toFixedMaxCy 格式化
    const precision = coinPricePrecisionCurrency && coinPricePrecisionCurrency > 0 ? coinPricePrecisionCurrency : 8;
    return Big(availableBalance).toFixedMaxCy(precision);
  }, [availableBalance, coinPricePrecisionCurrency]);

  const [tradeType, setTradeType] = useState<TradeTypeEnum>(TradeTypeEnum.limit);
  const [tradeSide, setTradeSide] = useState<TradeSideEnum>(TradeSideEnum.buy);
  const [leverage, setLeverage] = useState("");
  const [price, setPrice] = useState("");
  const [orderInputType, setOrderInputType] = useState<OrderInputTypeEnum>(OrderInputTypeEnum.quantity);
  const [inputValue, setInputValue] = useState(""); // 输入值（根据下单类型不同，可能是数量/开仓价值/保证金）
  const isPriceInitialized = useRef<boolean>(false); // 标记价格是否已初始化
  const [loading, setLoading] = useState(false); // 提交加载状态
  const [choiceSheetOpen, setChoiceSheetOpen] = useState(false);
  const [depositDrawerOpen, setDepositDrawerOpen] = useState(false);
  const [insufficientBalanceModalOpen, setInsufficientBalanceModalOpen] = useState(false);
  const quantityInputRef = useRef<XtInputNumberRefProps>(null);
  const [tpSlEnabled, setTpSlEnabled] = useState(false); // TP/SL 是否启用
  const [takeProfitPrice, setTakeProfitPrice] = useState(""); // 止盈价格（字符串）
  const [stopLossPrice, setStopLossPrice] = useState(""); // 止损价格（字符串）
  const [positions, setPositions] = useState<any[]>([]); // 持仓列表
  // 按数量/开仓价值下单时，调用 calc-margin 得到的保证金结果（含手续费）
  const [calcMarginResult, setCalcMarginResult] = useState<{ reservedMargin: number; fee: number; totalMargin: number } | null>(null);
  const [calcMarginLoading, setCalcMarginLoading] = useState(false);
  const calcMarginReqId = useRef(0);
  const calcMarginDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isSliderDragging, setIsSliderDragging] = useState(false);
  // 按保证金下单时，调用 calc-quantity 得到的数量
  const [calcQuantityResult, setCalcQuantityResult] = useState<number | null>(null);
  const [calcQuantityLoading, setCalcQuantityLoading] = useState(false);
  const calcQuantityDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 市价单不跟随 ticker 高频重算：仅在首次拿到有效价格时触发一次 calc-quantity
  const lastMarketCalcPriceRef = useRef<number>(0);
  const lastCalcQuantityReqKeyRef = useRef<string>("");

  // 当交易对变化时，重置表单内容
  const [prevName, setPrevName] = useState(name);
  if (prevName !== name) {
    setPrevName(name);
    setPrice("");
    setInputValue("");
    setLeverage("");
    setOrderInputType(OrderInputTypeEnum.quantity);
    setTradeSide(TradeSideEnum.buy);
    isPriceInitialized.current = false;
  }

  // 当交易类型变化时，重置初始化标记和价格
  const [prevTradeType, setPrevTradeType] = useState(tradeType);
  if (prevTradeType !== tradeType) {
    setPrevTradeType(tradeType);
    setPrice("");
    isPriceInitialized.current = false;
  }

  // 获取K线颜色方案
  const [klineColorScheme, setKlineColorScheme] = useState<"green_up" | "red_up">(() => {
    const savedScheme = Storage.get("klineColorScheme") as "green_up" | "red_up";
    return savedScheme === "red_up" || savedScheme === "green_up" ? savedScheme : "green_up";
  });

  // 监听K线颜色方案变化
  useEffect(() => {
    const handleColorSchemeChange = () => {
      const savedScheme = Storage.get("klineColorScheme") as "green_up" | "red_up";
      const currentScheme = savedScheme === "red_up" || savedScheme === "green_up" ? savedScheme : "green_up";
      setKlineColorScheme(currentScheme);
    };
    
    window.addEventListener("klineColorSchemeChange", handleColorSchemeChange);
    return () => {
      window.removeEventListener("klineColorSchemeChange", handleColorSchemeChange);
    };
  }, []);

  // 获取当前币种
  const currency = name ? name.split("_")[1].toLowerCase() : "usdt"; // CFD 使用计价币种（USDT）

  // 处理价格输入
  const handleInputPrice = useCallback((val: string) => {
    setPrice(val);
    // 用户输入后，标记为已初始化，不再自动更新
    if (val) {
      isPriceInitialized.current = true;
    }
  }, []);

  // 处理输入值变化
  const handleInputValue = useCallback((val: string) => {
    setInputValue(val);
  }, []);

  // 处理止盈价格输入，过滤非数字
  const handleTakeProfitInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let newVal = e.target.value;
    
    // 去掉千分位逗号
    newVal = newVal.replace(/,/g, "");
    
    // 允许空字符串
    if (newVal === "") {
      setTakeProfitPrice("");
      return;
    }
    
    // 只允许数字和小数点，允许小数点开头（如 .5）
    if (!/^[0-9]*\.?[0-9]*$/.test(newVal)) {
      return;
    }
    
    // 不允许多个小数点
    if ((newVal.match(/\./g) || []).length > 1) {
      return;
    }
    
    // 不允许以0开头（除了0.或0或.开头）
    if (/^0[0-9]/.test(newVal)) {
      return;
    }
    
    // 限制小数位数（最多8位）
    if (/\./.test(newVal)) {
      const decimalPart = newVal.split(".")[1];
      if (decimalPart && decimalPart.length > 8) {
        return;
      }
    }
    
    setTakeProfitPrice(newVal);
  }, []);

  // 处理止损价格输入，过滤非数字
  const handleStopLossInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let newVal = e.target.value;
    
    // 去掉千分位逗号
    newVal = newVal.replace(/,/g, "");
    
    // 允许空字符串
    if (newVal === "") {
      setStopLossPrice("");
      return;
    }
    
    // 只允许数字和小数点，允许小数点开头（如 .5）
    if (!/^[0-9]*\.?[0-9]*$/.test(newVal)) {
      return;
    }
    
    // 不允许多个小数点
    if ((newVal.match(/\./g) || []).length > 1) {
      return;
    }
    
    // 不允许以0开头（除了0.或0或.开头）
    if (/^0[0-9]/.test(newVal)) {
      return;
    }
    
    // 限制小数位数（最多8位）
    if (/\./.test(newVal)) {
      const decimalPart = newVal.split(".")[1];
      if (decimalPart && decimalPart.length > 8) {
        return;
      }
    }
    
    setStopLossPrice(newVal);
  }, []);

    // 从 store.cfdView.cfdTicker 获取当前价格（高频数据独立 store，避免整页重渲染）
    const currentPrice = useMemo(() => {
      const ticker = store.cfdView.cfdTicker;
      if (ticker && ticker.c && ticker.s === name) {
        return String(ticker.c);
      }
      return "0";
    }, [store.cfdView.cfdTicker, name]);

  // 获取当前使用的价格（市价/限价/触发价）
  const usedPrice = useMemo(() => {
    // 限价单必须使用输入的价格，市价单使用当前价格
    if (tradeType === TradeTypeEnum.limit) {
      return price && price.trim() ? Big(price) : Big(0);
    }
    return Big(currentPrice || 0);
  }, [price, currentPrice, tradeType]);

  // 按保证金下单：在保证金输入变化时调用 calc-quantity；
  // 限价单还需在价格变化时重算，买卖方向变化也应重算，避免首屏价格初始化后结果滞后
  useEffect(() => {
    if (calcQuantityDebounceTimer.current) {
      clearTimeout(calcQuantityDebounceTimer.current);
      calcQuantityDebounceTimer.current = null;
    }
    // 交互优先：拖动过程中仅更新输入值，不发 calc-quantity；松手后再重算
    if (isSliderDragging) {
      return;
    }
    if (orderInputType !== OrderInputTypeEnum.margin) {
      setCalcQuantityResult(null);
      lastMarketCalcPriceRef.current = 0;
      lastCalcQuantityReqKeyRef.current = "";
      return;
    }
    if (!isLogin || !name || !inputValue || Big(inputValue).lte(0) || !leverage || Number(leverage) <= 0) {
      setCalcQuantityResult(null);
      setCalcQuantityLoading(false);
      lastMarketCalcPriceRef.current = 0;
      lastCalcQuantityReqKeyRef.current = "";
      return;
    }
    const marginNum = Number(inputValue);
    const leverageNum = Number(leverage);

    // 限价：用用户输入价格；市价：用当前价格（但不跟随 ticker 高频触发）
    const priceNum =
      tradeType === TradeTypeEnum.limit
        ? Number(price || 0)
        : Number(currentPrice || 0);
    if (!priceNum || priceNum <= 0 || Number.isNaN(priceNum)) {
      setCalcQuantityResult(null);
      setCalcQuantityLoading(false);
      return;
    }
    const reqKey = JSON.stringify({
      symbol: name,
      margin: marginNum,
      leverage: leverageNum,
      priceType: tradeType === TradeTypeEnum.limit ? 1 : 2,
      side: tradeSide === TradeSideEnum.buy ? 1 : 2,
      tradeType,
      // limit 下单需要跟随用户输入的价格重算；market 下单不把价格纳入 key，避免 ticker 变化导致频繁请求
      limitPrice: tradeType === TradeTypeEnum.limit ? priceNum : undefined,
    });
    if (tradeType !== TradeTypeEnum.limit) {
      // 仅当“参数不变但价格在跳动”时跳过，避免 ticker 高频触发；若保证金/杠杆/方向变化仍需重算
      if (lastCalcQuantityReqKeyRef.current === reqKey && lastMarketCalcPriceRef.current > 0) {
        return;
      }
      lastMarketCalcPriceRef.current = priceNum;
    }
    lastCalcQuantityReqKeyRef.current = reqKey;

    setCalcQuantityLoading(true);
    calcQuantityDebounceTimer.current = setTimeout(() => {
      post_cfd_calcQuantity({
        data: {
          symbol: name,
          margin: marginNum,
          leverage: leverageNum,
          priceType: tradeType === TradeTypeEnum.limit ? 1 : 2,
          price: priceNum,
          side: tradeSide === TradeSideEnum.buy ? 1 : 2,
        },
        errorPop: false,
        autoErrorModal: false,
      })
        .then((res: any) => {
          const data = res?.result ?? res?.data ?? res;
          const q = data?.quantity;
          if (q != null && !Number.isNaN(Number(q))) {
            setCalcQuantityResult(Number(q));
          } else {
            setCalcQuantityResult(null);
          }
        })
        .catch(() => setCalcQuantityResult(null))
        .finally(() => setCalcQuantityLoading(false));
    }, 180);
    // 市价单价格高频变化时不随 ticker 频繁重算；仅在限价单按用户输入价格重算
    return () => {
      if (calcQuantityDebounceTimer.current) {
        clearTimeout(calcQuantityDebounceTimer.current);
        calcQuantityDebounceTimer.current = null;
      }
    };
  }, [orderInputType, inputValue, name, leverage, isLogin, tradeSide, tradeType, price, currentPrice, isSliderDragging]);

  // 按数量/开仓价值下单：数量/价值变化时调用 calc-margin；
  // 限价单价格或方向变化时也重算，避免首次进入页面时 cost 使用旧口径
  useEffect(() => {
    if (calcMarginDebounceTimer.current) {
      clearTimeout(calcMarginDebounceTimer.current);
      calcMarginDebounceTimer.current = null;
    }
    // 交互优先：拖动过程中只更新输入，不发 calc-margin；松手后再算
    if (isSliderDragging) {
      return;
    }
    const isByQuantityOrValue = orderInputType === OrderInputTypeEnum.quantity || orderInputType === OrderInputTypeEnum.value;
    if (!isByQuantityOrValue) {
      calcMarginReqId.current += 1;
      setCalcMarginLoading(false);
      setCalcMarginResult(null);
      return;
    }
    if (!isLogin || !name || !inputValue || Big(inputValue).lte(0) || !leverage || Number(leverage) <= 0) {
      calcMarginReqId.current += 1;
      setCalcMarginLoading(false);
      setCalcMarginResult(null);
      return;
    }
    let quantityNum: number;
    if (orderInputType === OrderInputTypeEnum.quantity) {
      quantityNum = Number(inputValue);
    } else {
      const priceNum = usedPrice.gt(0) ? usedPrice.toNumber() : Number(currentPrice || 0);
      if (!priceNum || priceNum <= 0) {
        calcMarginReqId.current += 1;
        setCalcMarginLoading(false);
        setCalcMarginResult(null);
        return;
      }
      quantityNum = Number(inputValue) / priceNum;
    }
    if (quantityNum <= 0 || Number.isNaN(quantityNum)) {
      calcMarginReqId.current += 1;
      setCalcMarginLoading(false);
      setCalcMarginResult(null);
      return;
    }
    const priceNum = usedPrice.gt(0) ? usedPrice.toNumber() : Number(currentPrice || 0);
    const reqId = ++calcMarginReqId.current;
    setCalcMarginLoading(true);
    calcMarginDebounceTimer.current = setTimeout(() => {
      post_cfd_calcMargin({
        data: {
          symbol: name,
          quantity: quantityNum,
          leverage: Number(leverage),
          priceType: tradeType === TradeTypeEnum.limit ? 1 : 2,
          price: priceNum,
          side: tradeSide === TradeSideEnum.buy ? 1 : 2,
        },
        errorPop: false,
      })
        .then((res: any) => {
          if (reqId !== calcMarginReqId.current) return;
          const data = res?.result ?? res?.data ?? res;
          const reserved = data?.reservedMargin;
          const fee = data?.fee;
          const total = data?.totalMargin;
          if (reserved != null && fee != null && total != null) {
            setCalcMarginResult({
              reservedMargin: Number(reserved),
              fee: Number(fee),
              totalMargin: Number(total),
            });
          } else {
            setCalcMarginResult(null);
          }
        })
        .catch(() => {
          if (reqId !== calcMarginReqId.current) return;
          setCalcMarginResult(null);
        })
        .finally(() => {
          if (reqId !== calcMarginReqId.current) return;
          setCalcMarginLoading(false);
        });
    }, 250);
    // 市价单价格高频变化时不随 ticker 频繁重算；仅在限价单按用户输入价格重算
    return () => {
      if (calcMarginDebounceTimer.current) {
        clearTimeout(calcMarginDebounceTimer.current);
        calcMarginDebounceTimer.current = null;
      }
    };
  }, [orderInputType, inputValue, name, leverage, isLogin, tradeSide, tradeType, price, isSliderDragging]);

  // 当前模式的手续费率（用于 totalMargin 驱动滑块的本地兜底换算）
  const pendingOrderFeeRate = useMemo(() => {
    const feeRate = Number(cfdConfig.pendingOrderFee);
    return Number.isFinite(feeRate) && feeRate > 0 ? feeRate : 0;
  }, [cfdConfig.pendingOrderFee]);

  // totalMargin -> quantity（与 iOS 口径对齐：按总成本反推数量）
  const quantityFromTotalMargin = useCallback(
    (totalMargin: any) => {
      const totalMarginNum = Big(totalMargin || 0);
      const priceNum = usedPrice;
      const leverageNum = Big(leverage || 1);
      if (!totalMarginNum.gt(0) || !priceNum.gt(0) || !leverageNum.gt(0)) return Big(0);
      const denominator = priceNum.times(Big(1).div(leverageNum).plus(pendingOrderFeeRate));
      if (!denominator.gt(0)) return Big(0);
      return totalMarginNum.div(denominator);
    },
    [usedPrice, leverage, pendingOrderFeeRate]
  );

  // 当前输入对应的 totalMargin（滑块百分比统一以 totalMargin / availableBalance 计算）
  const sliderTotalMarginValue = useMemo(() => {
    if (!inputValue) return "";
    const inputNum = Big(inputValue || 0);
    const priceNum = usedPrice;
    const leverageNum = Big(leverage || 1);
    if (!inputNum.gt(0)) return "";

    switch (orderInputType) {
      case OrderInputTypeEnum.margin:
        return inputNum.toFixed(coinPricePrecisionMarket || 2);
      case OrderInputTypeEnum.quantity: {
        if (calcMarginResult?.totalMargin != null && calcMarginResult.totalMargin > 0) {
          return Big(calcMarginResult.totalMargin).toFixed(coinPricePrecisionMarket || 2);
        }
        if (!priceNum.gt(0) || !leverageNum.gt(0)) return "";
        const totalMargin = inputNum.times(priceNum).times(Big(1).div(leverageNum).plus(pendingOrderFeeRate));
        return totalMargin.toFixed(coinPricePrecisionMarket || 2);
      }
      case OrderInputTypeEnum.value: {
        if (calcMarginResult?.totalMargin != null && calcMarginResult.totalMargin > 0) {
          return Big(calcMarginResult.totalMargin).toFixed(coinPricePrecisionMarket || 2);
        }
        if (!leverageNum.gt(0)) return "";
        const totalMargin = inputNum.times(Big(1).div(leverageNum).plus(pendingOrderFeeRate));
        return totalMargin.toFixed(coinPricePrecisionMarket || 2);
      }
      default:
        return "";
    }
  }, [inputValue, orderInputType, usedPrice, leverage, calcMarginResult, pendingOrderFeeRate, coinPricePrecisionMarket]);

  // 滑块变化处理（与 iOS 对齐：滑块按 totalMargin 百分比分配，再反推当前输入单位）
  const handleSliderChange = useCallback(
    (val: string, percent: number) => {
      let totalMarginValue = val;
      if (!+val && !percent) {
        totalMarginValue = "";
      }
      const totalMarginNum = Big(totalMarginValue || 0);
      const priceNum = usedPrice;

      let convertedValue = "";
      switch (orderInputType) {
        case OrderInputTypeEnum.quantity: {
          const quantityNum = quantityFromTotalMargin(totalMarginNum);
          convertedValue = quantityNum.gt(0) ? quantityNum.toFixed(coinQuantityPrecisionMarket || 8) : "";
          break;
        }
        case OrderInputTypeEnum.value: {
          const quantityNum = quantityFromTotalMargin(totalMarginNum);
          convertedValue = quantityNum.gt(0) && priceNum.gt(0) ? quantityNum.times(priceNum).toFixed(coinPricePrecisionMarket || 2) : "";
          break;
        }
        case OrderInputTypeEnum.margin:
          convertedValue = totalMarginNum.gt(0) ? totalMarginNum.toFixed(coinPricePrecisionMarket || 2) : "";
          break;
      }
      setInputValue(convertedValue);
    },
    [orderInputType, usedPrice, quantityFromTotalMargin, coinQuantityPrecisionMarket, coinPricePrecisionMarket]
  );

  // 杠杆选项 - 按25步进生成：1, 25, 50, 75, 100, 125, 150, 175, 200...
  const leverageOptions = useMemo(() => {
    if (cfdConfig.leverageRange) {
      const { min, max } = cfdConfig.leverageRange;
      const options: string[] = [];
      
      // 从1开始，按25步进生成，直到超过max
      for (let lev = 1; lev <= max; lev += lev === 1 ? 24 : 25) {
        if (lev >= min && lev <= max) {
          options.push(String(lev));
        }
      }
      
      // 确保包含最小和最大值
      if (min > 1 && !options.includes(String(min))) {
        options.unshift(String(min));
        options.sort((a, b) => Number(a) - Number(b));
      }
      if (!options.includes(String(max))) {
        options.push(String(max));
        options.sort((a, b) => Number(a) - Number(b));
      }
      
      return options.length > 0 ? options : ["1"];
    }
    // 默认杠杆选项：1, 25, 50, 75, 100, 125, 150, 175, 200
    return ["1", "25", "50", "75", "100", "125", "150", "175", "200"];
  }, [cfdConfig.leverageRange]);

  // 根据配置设置默认杠杆为 max(最小杠杆, 50)
  useEffect(() => {
    if (cfdConfig.leverageRange) {
      const { min, max } = cfdConfig.leverageRange;
      const defaultLeverage = Math.max(min, 50);
      // 如果当前杠杆为空或不在范围内，设置为默认杠杆
      if (!leverage || Number(leverage) < min || Number(leverage) > max) {
        setLeverage(String(defaultLeverage));
      }
    } else if (!leverage) {
      // 如果没有配置，默认使用100
      setLeverage("100");
    }
  }, [cfdConfig.leverageRange, leverage]);

  // 获取持仓数据
  const apiReqPositionArg = useMemo(() => {
    const spotSymbol = name.replace(/_cfd$/i, "");
    const propTrading = isPropTradingCfdTradeRoute(router);
    return {
      fn: get_cfd_positions,
      config: {
        params: {
          symbol: spotSymbol,
          limit: 100,
          page: 1,
          propTrading,
        },
      },
      success: (data: any) => {
        const items = data?.items || [];
        setPositions(items);
      },
    };
  }, [name, router.pathname, router.asPath, router.query]);
  const apiReqPosition = useAxiosCancelFun(apiReqPositionArg);

  // 当登录状态或交易对变化时，获取持仓数据
  useEffect(() => {
    if (isLogin && name) {
      apiReqPosition();
    } else {
      setPositions([]);
    }
  }, [isLogin, name, apiReqPosition]);


  // 当价格更新且限价单价格为空时，自动设置限价单价格
  useEffect(() => {
    if (currentPrice && currentPrice !== "0" && !isPriceInitialized.current && !price && tradeType === TradeTypeEnum.limit) {
      setPrice(currentPrice);
      isPriceInitialized.current = true;
    }
  }, [currentPrice, price, tradeType]);

  // 当点击盘口价格时，更新限价单价格
  useEffect(() => {
    if (tradeRecentOnce && tradeRecentOnce.isClick && tradeType === TradeTypeEnum.limit) {
      setPrice(tradeRecentOnce.p);
      isPriceInitialized.current = true; // 点击盘口价格后，标记为已初始化
      store.trade.updateState({
        tradeRecentOnce: {
          ...tradeRecentOnce,
          isClick: false,
        },
      });
    }
  }, [tradeRecentOnce, tradeType]);

  // 杠杆下拉菜单
  const leverageDropdownItems: MenuProps["items"] = useMemo(() => {
    return leverageOptions.map((lev) => ({
      key: lev,
      label: (
        <div
          onClick={() => {
            setLeverage(lev);
          }}
        >
          {lev}X
        </div>
      ),
    }));
  }, [leverageOptions]);

  // 交易类型下拉菜单
  const tradeTypeDropdownItems: MenuProps["items"] = useMemo(() => {
    return [
      {
        key: TradeTypeEnum.limit,
        label: <div onClick={() => setTradeType(TradeTypeEnum.limit)}>{t("cfd.limitOrder")}</div>,
      },
      {
        key: TradeTypeEnum.market,
        label: <div onClick={() => setTradeType(TradeTypeEnum.market)}>{t("cfd.marketOrder")}</div>,
      },
    ];
  }, []);

  // 根据下单类型计算数量、开仓价值和保证金；按数量/价值时用 calc-margin 结果，按保证金时用 calc-quantity 结果
  const { quantity, value, margin } = useMemo(() => {
    const inputNum = Big(inputValue || 0);
    const leverageNum = Big(leverage || 1);
    const priceNum = usedPrice;
    const quantityPrecision = coinQuantityPrecisionMarket && coinQuantityPrecisionMarket > 0 ? coinQuantityPrecisionMarket : 8;
    const pricePrecision = coinPricePrecisionMarket && coinPricePrecisionMarket > 0 ? coinPricePrecisionMarket : 2;
    const marginPrecision = cfdConfig.marginPrecision !== undefined ? cfdConfig.marginPrecision : pricePrecision;

    let calculatedQuantity = Big(0);
    let calculatedValue = Big(0);
    let calculatedMargin = Big(0);

    switch (orderInputType) {
      case OrderInputTypeEnum.margin:
        // 按保证金：数量来自 calc-quantity 接口
        if (calcQuantityResult != null && calcQuantityResult > 0) {
          calculatedQuantity = Big(calcQuantityResult);
          calculatedValue = calculatedQuantity.times(priceNum);
          calculatedMargin = inputNum;
        } else if (priceNum.gt(0)) {
          calculatedMargin = inputNum;
          calculatedValue = calculatedMargin.times(leverageNum);
          calculatedQuantity = priceNum.gt(0) ? calculatedValue.div(priceNum) : Big(0);
        }
        break;
      case OrderInputTypeEnum.quantity:
      case OrderInputTypeEnum.value:
        // 按数量/开仓价值：保证金、总扣款来自 calc-margin 接口
        if (calcMarginResult != null && calcMarginResult.totalMargin >= 0) {
          calculatedMargin = Big(calcMarginResult.totalMargin);
          if (orderInputType === OrderInputTypeEnum.quantity) {
            calculatedQuantity = inputNum;
            calculatedValue = calculatedQuantity.times(priceNum);
          } else {
            calculatedValue = inputNum;
            calculatedQuantity = priceNum.gt(0) ? calculatedValue.div(priceNum) : Big(0);
          }
        } else if (priceNum.gt(0)) {
          if (orderInputType === OrderInputTypeEnum.quantity) {
            calculatedQuantity = inputNum;
            calculatedValue = calculatedQuantity.times(priceNum);
            calculatedMargin = leverageNum.gt(0) ? calculatedValue.div(leverageNum) : Big(0);
          } else {
            calculatedValue = inputNum;
            calculatedQuantity = priceNum.gt(0) ? calculatedValue.div(priceNum) : Big(0);
            calculatedMargin = leverageNum.gt(0) ? calculatedValue.div(leverageNum) : Big(0);
          }
        }
        break;
    }

    const quantityStr = calculatedQuantity.gt(0) ? calculatedQuantity.toFixedCy(quantityPrecision) : (orderInputType === OrderInputTypeEnum.margin && calcQuantityLoading ? "..." : "--");
    const valueStr = calculatedValue.gt(0) ? calculatedValue.toFixedCy(pricePrecision) : (orderInputType !== OrderInputTypeEnum.margin && calcMarginLoading ? "..." : "--");
    const marginStr = calculatedMargin.gt(0) ? calculatedMargin.toFixedCy(marginPrecision) : (orderInputType !== OrderInputTypeEnum.margin && calcMarginLoading ? "..." : "--");

    return {
      quantity: quantityStr,
      value: valueStr,
      margin: marginStr,
    };
  }, [inputValue, leverage, usedPrice, orderInputType, calcQuantityResult, calcQuantityLoading, calcMarginResult, calcMarginLoading, coinQuantityPrecisionMarket, coinPricePrecisionMarket, cfdConfig.marginPrecision]);

  // 止盈止损范围显示文本
  const tpSlRangeText = useMemo(() => {
    if (cfdConfig.tpSlRange) {
      const { max, min } = cfdConfig.tpSlRange;
      return `+${max * 100}%/-${min * 100}%`;
    }
    return "+XXX/-XXX";
  }, [cfdConfig.tpSlRange]);

  // 获取买一价和卖一价（用于限价单计算），从 cfdView 读取避免整页重渲染
  const { askPrice, bidPrice } = useMemo(() => {
    const { depthAsks, depthBids } = store.cfdView;
    // 卖一价（买涨时使用，从卖盘买入）
    const ask = depthAsks && depthAsks.length > 0 && depthAsks[0] && depthAsks[0][0] ? Big(depthAsks[0][0]) : Big(0);
    // 买一价（买跌时使用，从买盘卖出）
    const bid = depthBids && depthBids.length > 0 && depthBids[0] && depthBids[0][0] ? Big(depthBids[0][0]) : Big(0);
    return {
      askPrice: ask,
      bidPrice: bid,
    };
  }, [store.cfdView.depthAsks, store.cfdView.depthBids]);

  // 计算当前交易对的持仓占用的保证金（按方向分类）
  // 专业版 Max Open 仅用占用保证金计算，与持仓列表展示无关。后端持仓 side：2=买涨(LONG) 1=卖跌(SHORT)（与开仓接口 side 一致）；归一化 Number(side) 兼容字符串
  const { longPositionMargin, shortPositionMargin } = useMemo(() => {
    const spotSymbol = name.replace(/_cfd$/i, "").toUpperCase();
    let longMargin = Big(0);
    let shortMargin = Big(0);
    
    positions.forEach((item: any) => {
      const itemSymbol = (item.symbol || "").replace(/_cfd$/i, "").toUpperCase();
      if (itemSymbol !== spotSymbol) return;
      const margin = Big(item.margin || 0);
      if (margin.lte(0)) return;
      const side = Number(item.side);
      if (side === 2) {
        longMargin = longMargin.plus(margin); // 2=买涨(LONG)
      } else if (side === 1) {
        shortMargin = shortMargin.plus(margin); // 1=卖跌(SHORT)
      }
    });
    
    return {
      longPositionMargin: longMargin,
      shortPositionMargin: shortMargin,
    };
  }, [positions, name]);

  // 计算买涨的 Max Open（最大可开仓价值 = (可用余额 - 多仓占用保证金) * 杠杆）
  const maxOpenBuy = useMemo(() => {
    const availableBalanceNum = Big(availableBalance || 0);
    const leverageNum = Big(leverage || 1);
    // 可用保证金 = 总可用余额 - 多仓占用的保证金
    const availableForLong = availableBalanceNum.minus(longPositionMargin);
    const maxOpenValue = availableForLong.gt(0) ? availableForLong.times(leverageNum) : Big(0);
    const pricePrecision = coinPricePrecisionMarket && coinPricePrecisionMarket > 0 ? coinPricePrecisionMarket : 2;
    return maxOpenValue.gt(0) ? maxOpenValue.toFixedCy(pricePrecision) : "0.00";
  }, [availableBalance, leverage, longPositionMargin, coinPricePrecisionMarket]);

  // 计算买跌的 Max Open（最大可开仓价值 = (可用余额 - 空仓占用保证金) * 杠杆）
  const maxOpenSell = useMemo(() => {
    const availableBalanceNum = Big(availableBalance || 0);
    const leverageNum = Big(leverage || 1);
    // 可用保证金 = 总可用余额 - 空仓占用的保证金
    const availableForShort = availableBalanceNum.minus(shortPositionMargin);
    const maxOpenValue = availableForShort.gt(0) ? availableForShort.times(leverageNum) : Big(0);
    const pricePrecision = coinPricePrecisionMarket && coinPricePrecisionMarket > 0 ? coinPricePrecisionMarket : 2;
    return maxOpenValue.gt(0) ? maxOpenValue.toFixedCy(pricePrecision) : "0.00";
  }, [availableBalance, leverage, shortPositionMargin, coinPricePrecisionMarket]);

  // 计算买涨的 Cost（按数量/价值时优先用 calc-margin 的 totalMargin 即总扣款，含手续费）
  const costBuy = useMemo(() => {
    if (margin === "--") return "0.00";
    if ((orderInputType === OrderInputTypeEnum.quantity || orderInputType === OrderInputTypeEnum.value) && calcMarginResult?.totalMargin != null && calcMarginResult.totalMargin >= 0) {
      const marginPrecision = cfdConfig.marginPrecision !== undefined ? cfdConfig.marginPrecision : (coinPricePrecisionMarket || 2);
      return Big(calcMarginResult.totalMargin).toFixedCy(marginPrecision);
    }
    const leverageNum = Big(leverage || 1);
    let priceForBuy = usedPrice;
    if (tradeType === TradeTypeEnum.limit) {
      if (price && price.trim()) priceForBuy = Big(price);
      else if (askPrice.gt(0)) priceForBuy = askPrice;
    }
    const inputNum = Big(inputValue || 0);
    if (inputNum.gt(0) && priceForBuy.gt(0) && leverageNum.gt(0)) {
      let calculatedMargin = Big(0);
      switch (orderInputType) {
        case OrderInputTypeEnum.quantity:
          calculatedMargin = leverageNum.gt(0) ? inputNum.times(priceForBuy).div(leverageNum) : Big(0);
          break;
        case OrderInputTypeEnum.value:
          calculatedMargin = leverageNum.gt(0) ? inputNum.div(leverageNum) : Big(0);
          break;
        case OrderInputTypeEnum.margin:
          calculatedMargin = inputNum;
          break;
      }
      if (calculatedMargin.gt(0)) {
        const marginPrecision = cfdConfig.marginPrecision !== undefined ? cfdConfig.marginPrecision : (coinPricePrecisionMarket || 2);
        return calculatedMargin.toFixedCy(marginPrecision);
      }
    }
    return margin;
  }, [margin, leverage, usedPrice, tradeType, askPrice, inputValue, orderInputType, price, calcMarginResult, cfdConfig.marginPrecision, coinPricePrecisionMarket]);

  // 计算买跌的 Cost（按数量/价值时优先用 calc-margin 的 totalMargin 即总扣款，含手续费）
  const costSell = useMemo(() => {
    if (margin === "--") return "0.00";
    if ((orderInputType === OrderInputTypeEnum.quantity || orderInputType === OrderInputTypeEnum.value) && calcMarginResult?.totalMargin != null && calcMarginResult.totalMargin >= 0) {
      const marginPrecision = cfdConfig.marginPrecision !== undefined ? cfdConfig.marginPrecision : (coinPricePrecisionMarket || 2);
      return Big(calcMarginResult.totalMargin).toFixedCy(marginPrecision);
    }
    const leverageNum = Big(leverage || 1);
    let priceForSell = usedPrice;
    if (tradeType === TradeTypeEnum.limit) {
      if (price && price.trim()) priceForSell = Big(price);
      else if (bidPrice.gt(0)) priceForSell = bidPrice;
    }
    const inputNum = Big(inputValue || 0);
    if (inputNum.gt(0) && priceForSell.gt(0) && leverageNum.gt(0)) {
      let calculatedMargin = Big(0);
      switch (orderInputType) {
        case OrderInputTypeEnum.quantity:
          calculatedMargin = leverageNum.gt(0) ? inputNum.times(priceForSell).div(leverageNum) : Big(0);
          break;
        case OrderInputTypeEnum.value:
          calculatedMargin = leverageNum.gt(0) ? inputNum.div(leverageNum) : Big(0);
          break;
        case OrderInputTypeEnum.margin:
          calculatedMargin = inputNum;
          break;
      }
      if (calculatedMargin.gt(0)) {
        const marginPrecision = cfdConfig.marginPrecision !== undefined ? cfdConfig.marginPrecision : (coinPricePrecisionMarket || 2);
        return calculatedMargin.toFixedCy(marginPrecision);
      }
    }
    return margin;
  }, [margin, leverage, usedPrice, tradeType, bidPrice, inputValue, orderInputType, price, calcMarginResult, cfdConfig.marginPrecision, coinPricePrecisionMarket]);

  // 下单类型下拉菜单
  const orderInputTypeDropdownItems: MenuProps["items"] = useMemo(() => {
    return [
      {
        key: OrderInputTypeEnum.quantity,
        label: (
          <div
            onClick={() => {
              // 切换类型时，将当前输入值转换为新类型对应的值
              const inputNum = Big(inputValue || 0);
              const priceNum = usedPrice;
              const leverageNum = Big(leverage || 1);
              
              let convertedValue = "";
              if (orderInputType === OrderInputTypeEnum.value) {
                // 从开仓价值转为数量
                convertedValue = priceNum.gt(0) ? inputNum.div(priceNum).toFixed(coinQuantityPrecisionMarket || 8) : "";
              } else if (orderInputType === OrderInputTypeEnum.margin) {
                // 从保证金转为数量
                if (priceNum.gt(0) && leverageNum.gt(0)) {
                  convertedValue = inputNum.times(leverageNum).div(priceNum).toFixed(coinQuantityPrecisionMarket || 8);
                }
              } else {
                convertedValue = inputValue;
              }
              
              setOrderInputType(OrderInputTypeEnum.quantity);
              setInputValue(convertedValue);
            }}
          >
            {coinQuantityUpperCase}
          </div>
        ),
      },
      {
        key: OrderInputTypeEnum.value,
        label: (
          <div
            onClick={() => {
              const inputNum = Big(inputValue || 0);
              const priceNum = usedPrice;
              const leverageNum = Big(leverage || 1);
              
              let convertedValue = "";
              if (orderInputType === OrderInputTypeEnum.quantity) {
                // 从数量转为开仓价值
                convertedValue = priceNum.gt(0) ? inputNum.times(priceNum).toFixed(coinPricePrecisionMarket || 2) : "";
              } else if (orderInputType === OrderInputTypeEnum.margin) {
                // 从保证金转为开仓价值
                convertedValue = leverageNum.gt(0) ? inputNum.times(leverageNum).toFixed(coinPricePrecisionMarket || 2) : "";
              } else {
                convertedValue = inputValue;
              }
              
              setOrderInputType(OrderInputTypeEnum.value);
              setInputValue(convertedValue);
            }}
          >
            {t("cfd.value")}
          </div>
        ),
      },
      {
        key: OrderInputTypeEnum.margin,
        label: (
          <div
            onClick={() => {
              const inputNum = Big(inputValue || 0);
              const priceNum = usedPrice;
              const leverageNum = Big(leverage || 1);
              
              let convertedValue = "";
              if (orderInputType === OrderInputTypeEnum.quantity) {
                // 从数量转为保证金
                if (priceNum.gt(0) && leverageNum.gt(0)) {
                  convertedValue = inputNum.times(priceNum).div(leverageNum).toFixed(coinPricePrecisionMarket || 2);
                }
              } else if (orderInputType === OrderInputTypeEnum.value) {
                // 从开仓价值转为保证金
                convertedValue = leverageNum.gt(0) ? inputNum.div(leverageNum).toFixed(coinPricePrecisionMarket || 2) : "";
              } else {
                convertedValue = inputValue;
              }
              
              setOrderInputType(OrderInputTypeEnum.margin);
              setInputValue(convertedValue);
            }}
          >
            {t("cfd.margin")}
          </div>
        ),
      },
    ];
  }, [orderInputType, inputValue, usedPrice, leverage, coinQuantityUpperCase, coinQuantityPrecisionMarket, coinPricePrecisionMarket]);


  // 提交开仓订单
  const handleSubmit = useCallback(() => {
    if (loading) return;
    if (!isLogin) {
      const query = "?backurl=" + encodeURIComponent(location.href);
      location.href = getUrl("/accounts/login" + query);
      return;
    }

    // 验证输入值
    const inputNum = Big(inputValue || 0);
    if (inputNum.lte(0)) {
      message.error(
        orderInputType === OrderInputTypeEnum.margin
          ? t("cfd.invalidMargin") || "Invalid margin"
          : t("cfd.invalidOpenQuantity")
      );
      return;
    }

    // 计算实际数量和保证金（从 quantity, value, margin 中获取）
    const leverageNum = Big(leverage || 1);
    // 限价单必须使用用户输入的价格，市价单使用当前价格
    let priceNum;
    if (tradeType === TradeTypeEnum.limit) {
      // 验证限价单价格
      if (!price || !price.trim()) {
        message.error(t("cfd.pleaseEnterPrice"));
        return;
      }
      try {
        priceNum = Big(price);
        if (priceNum.lte(0) || priceNum.toString() === "NaN" || priceNum.toString() === "Infinity") {
          message.error(t("cfd.pleaseEnterPrice"));
          return;
        }
      } catch (e) {
        message.error(t("cfd.pleaseEnterPrice"));
        return;
      }
    } else {
      priceNum = Big(currentPrice || 0);
      if (priceNum.lte(0)) {
        message.error(t("cfd.marketPriceUnavailable") || "Market price unavailable");
        return;
      }
    }
    
    let calculatedQuantity = Big(0);
    let calculatedMargin = Big(0);
    try {
      switch (orderInputType) {
        case OrderInputTypeEnum.quantity:
          calculatedQuantity = inputNum;
          // 按数量/价值时用 calc-margin 的 totalMargin 做余额校验（含手续费），否则用本地估算
          calculatedMargin = calcMarginResult?.totalMargin != null && calcMarginResult.totalMargin >= 0
            ? Big(calcMarginResult.totalMargin)
            : (leverageNum.gt(0) ? inputNum.times(priceNum).div(leverageNum) : Big(0));
          break;
        case OrderInputTypeEnum.value:
          calculatedQuantity = inputNum.div(priceNum);
          calculatedMargin = calcMarginResult?.totalMargin != null && calcMarginResult.totalMargin >= 0
            ? Big(calcMarginResult.totalMargin)
            : (leverageNum.gt(0) ? inputNum.div(leverageNum) : Big(0));
          break;
        case OrderInputTypeEnum.margin:
          // 按保证金时优先用 calc-quantity 接口返回的数量
          if (calcQuantityResult != null && calcQuantityResult > 0) {
            calculatedQuantity = Big(calcQuantityResult);
          } else {
            calculatedQuantity = leverageNum.gt(0) ? inputNum.times(leverageNum).div(priceNum) : Big(0);
          }
          calculatedMargin = inputNum;
          break;
      }
    } catch (e) {
      console.error("【计算数量或保证金失败】", e);
      message.error(t("cfd.invalidOpenQuantity"));
      return;
    }
    const quantityStr = calculatedQuantity.toString();
    if (calculatedQuantity.lte(0) || quantityStr === "NaN" || quantityStr === "Infinity" || quantityStr === "-Infinity") {
      message.error(t("cfd.invalidOpenQuantity"));
      return;
    }
    const quantityPrecision = coinQuantityPrecisionMarket && coinQuantityPrecisionMarket > 0 ? coinQuantityPrecisionMarket : 8;
    const roundedQuantity = Number(calculatedQuantity.toFixed(quantityPrecision));
    if (roundedQuantity <= 0) {
      message.error(t("cfd.quantityTooSmall"));
      return;
    }
    const marginStr = calculatedMargin.toString();
    if (calculatedMargin.lte(0) || marginStr === "NaN" || marginStr === "Infinity" || marginStr === "-Infinity") {
      message.error(t("cfd.invalidMargin") || "Invalid margin");
      return;
    }
    const availableBalanceNum = Big(availableBalance || 0);
    if (availableBalanceNum.lt(calculatedMargin)) {
      setInsufficientBalanceModalOpen(true);
      return;
    }

    // 检查是否需要二次确认（默认开启）
    const orderConfirm_limit = Storage.get("orderConfirm_limit");
    const orderConfirm_market = Storage.get("orderConfirm_market");
    // 如果 Storage 中没有值，默认为 true（开启）
    const needConfirm = tradeType === TradeTypeEnum.limit 
      ? (orderConfirm_limit === undefined || orderConfirm_limit === null ? true : orderConfirm_limit !== false)
      : (orderConfirm_market === undefined || orderConfirm_market === null ? true : orderConfirm_market !== false);

    // 股票/大宗(plateId 260/261)：若最新价格已超过半小时未更新，开仓前二次确认（时间取 socket ticker 与 kline 接口最新一根中较新者，进页后 socket 未推送过时用 kline 时间）
    const plates = store.market.currentConfig?.plates;
    const isStockOrCommodity = Array.isArray(plates) && (plates.includes(260) || plates.includes(261));
    const HALF_HOUR_MS = 30 * 60 * 1000;
    const tickerTime = store.cfdView.cfdTickerUpdateTime || 0;
    const klineTime = store.cfdView.lastKlineBarTime || 0;
    const effectivePriceUpdateTime = Math.max(tickerTime, klineTime);
    const isPriceStale = effectivePriceUpdateTime === 0 || Date.now() - effectivePriceUpdateTime > HALF_HOUR_MS;
    const needNonTradingHoursConfirm = isStockOrCommodity && isPriceStale;

    // 提交订单的函数
    const submitOrder = () => {
      setLoading(true);
      
      // 确保 quantity 是有效的数字（使用 toFixed 而不是 toFixedCy，因为 toFixedCy 会添加千分位符）
      const quantityPrecision = coinQuantityPrecisionMarket || 8;
      const quantityNum = Number(calculatedQuantity.toFixed(quantityPrecision));
      
      if (isNaN(quantityNum) || quantityNum <= 0) {
        message.error(t("cfd.invalidOpenQuantity"));
        setLoading(false);
        return;
      }
      
      const marginPrecisionForOrder = cfdConfig.marginPrecision !== undefined ? cfdConfig.marginPrecision : (coinPricePrecisionMarket || 2);
      const orderData: any = {
        symbol: name,
        side: tradeSide === TradeSideEnum.buy ? 1 : 2, // 1=买(LONG), 2=卖(SHORT)
        priceType: tradeType === TradeTypeEnum.limit ? 1 : 2, // 1=限价, 2=市价
        quantity: quantityNum,
        leverage: Number(leverage),
        keep: 0, // 0=关闭持仓过夜，1=开启（暂时固定为0，后续可添加开关）
      };
      if (orderInputType === OrderInputTypeEnum.margin && calculatedMargin.gt(0)) {
        orderData.margin = Number(calculatedMargin.toFixed(marginPrecisionForOrder)); // 按保证金下单时传 margin，与 quantity 同时传时接口可优先按 margin
      }

      // 限价单需要传价格
      if (tradeType === TradeTypeEnum.limit && price) {
        const priceNum = Number(price);
        if (isNaN(priceNum) || priceNum <= 0) {
          message.error(t("cfd.pleaseEnterPrice"));
          setLoading(false);
          return;
        }
        orderData.price = priceNum;
      }

      // 止盈止损价格
      // 只有勾选了 TP/SL 才处理，并且只传有值的价格
      // 如果没有勾选 TP/SL，则不传这两个字段
      if (tpSlEnabled) {
        // 处理止盈价格
        if (takeProfitPrice && takeProfitPrice.trim()) {
          const cleanTakeProfit = takeProfitPrice.replace(/,/g, "").trim();
          if (cleanTakeProfit) {
            try {
              const tpPriceNum = Big(cleanTakeProfit);
              if (tpPriceNum.gt(0)) {
                orderData.takeProfitPrice = Number(cleanTakeProfit);
              }
            } catch (e) {
              console.error("【止盈价格格式错误】", e);
            }
          }
        }
        
        // 处理止损价格
        if (stopLossPrice && stopLossPrice.trim()) {
          const cleanStopLoss = stopLossPrice.replace(/,/g, "").trim();
          if (cleanStopLoss) {
            try {
              const slPriceNum = Big(cleanStopLoss);
              if (slPriceNum.gt(0)) {
                orderData.stopLossPrice = Number(cleanStopLoss);
              }
            } catch (e) {
              console.error("【止损价格格式错误】", e);
            }
          }
        }
      }

      const openReq = usePropOpenApi
        ? post_prop_open_position({
            data: build_prop_open_position_body(
              name,
              {
                side: orderData.side,
                quantity: orderData.quantity,
                priceType: orderData.priceType,
                price: orderData.price,
                leverage: orderData.leverage,
                keep: orderData.keep,
                takeProfitPrice: orderData.takeProfitPrice,
                stopLossPrice: orderData.stopLossPrice,
              },
              { propTradingPage: usePropOpenApi },
            ),
            errorPop: true,
            successPop: () => message.success(t("cfd.placeOrderSuccess")),
          })
        : post_cfd_openPosition({
            data: orderData,
            errorPop: true,
            successPop: () => message.success(t("cfd.placeOrderSuccess")),
          });

      openReq
        .then((data) => {
          console.log("【CFD 开仓成功】", data);
          // 清空表单
          setInputValue("");
          setPrice("");
          isPriceInitialized.current = false;
          setTpSlEnabled(false);
          setTakeProfitPrice("");
          setStopLossPrice("");
          // 刷新持仓列表和订单列表
          onOpenPositionSuccess && onOpenPositionSuccess();
        })
        .catch((err) => {
          console.error("【CFD 开仓失败】", err);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    // 继续到订单确认或直接提交
    const proceedToOrderConfirmOrSubmit = () => {
      if (needConfirm) {
        let disableOrderConfirmQuickly = false;
        Modal.confirm({
          className: styles.orderConfirmModal,
          title: t("trade.orderConfirm"),
          width: 400,
          centered: true,
          closable: true,
          closeIcon: <XtSvg icon="close" />,
          icon: null,
          okText: t("confirm"),
          cancelText: t("cancel"),
          onOk: () => {
            if (disableOrderConfirmQuickly) {
              store.trade.updateState({
                orderConfirm_limit: false,
                orderConfirm_market: false,
              });
              Storage.set("orderConfirm_limit", false);
              Storage.set("orderConfirm_market", false);
            }
            return submitOrder();
          },
          content: (
            <div className={styles.orderConfirmContent}>
              <div className={styles.orderConfirmRow}>
                <div className={styles.orderConfirmLabel}>{t("cfd.direction")}</div>
                <div className={cx(styles.orderConfirmValue, tradeSide === TradeSideEnum.buy ? styles.buy : styles.sell)}>
                  {tradeSide === TradeSideEnum.buy ? t("cfd.buyUp") : t("cfd.buyDown")}
                </div>
              </div>
              <div className={styles.orderConfirmRow}>
                <div className={styles.orderConfirmLabel}>{t("cfd.priceType")}</div>
                <div className={styles.orderConfirmValue}>
                  {tradeType === TradeTypeEnum.limit ? t("cfd.limitOrder") : t("cfd.marketOrder")}
                </div>
              </div>
              {tradeType === TradeTypeEnum.limit && (
                <div className={styles.orderConfirmRow}>
                  <div className={styles.orderConfirmLabel}>{t("cfd.price")}</div>
                  <div className={styles.orderConfirmValue}>{price} {coinPriceUpperCase}</div>
                </div>
              )}
              <div className={styles.orderConfirmRow}>
                <div className={styles.orderConfirmLabel}>{t("cfd.leverage")}</div>
                <div className={styles.orderConfirmValue}>{leverage}X</div>
              </div>
              <div className={styles.orderConfirmRow}>
                <div className={styles.orderConfirmLabel}>{t("cfd.quantity")}</div>
                <div className={styles.orderConfirmValue}>
                  {calculatedQuantity.toFixedCy(coinQuantityPrecisionMarket || 8)} {coinQuantityUpperCase}
                </div>
              </div>
              <div className={styles.orderConfirmRow}>
                <div className={styles.orderConfirmLabel}>{t("cfd.openValue")}</div>
                <div className={styles.orderConfirmValue}>
                  {calculatedQuantity.times(priceNum).toFixedCy(coinPricePrecisionMarket || 2)} {coinPriceUpperCase}
                </div>
              </div>
              <div className={styles.orderConfirmRow}>
                <div className={styles.orderConfirmLabel}>{t("cfd.margin")}</div>
                <div className={styles.orderConfirmValue}>
                  {calculatedMargin.toFixedCy(coinPricePrecisionMarket || 2)} {coinPriceUpperCase}
                </div>
              </div>
              <div className={styles.quickConfirmToggle}>
                <Checkbox onChange={(e) => { disableOrderConfirmQuickly = !!e.target.checked; }}>
                  {t("cfd.skipOrderConfirmNext")}
                </Checkbox>
                <Tooltip title={t("cfd.reEnableOrderConfirmTip")} placement="top" overlayClassName={styles.quickConfirmTooltip}>
                  <span className={styles.quickConfirmInfoIcon}>i</span>
                </Tooltip>
              </div>
            </div>
          ),
        });
      } else {
        submitOrder();
      }
    };

    // 非交易时段（股票/大宗且价格超 30 分钟未更新）：先弹二次确认再走订单确认或提交
    if (needNonTradingHoursConfirm) {
      Modal.confirm({
        width: 400,
        centered: true,
        closable: true,
        closeIcon: <XtSvg icon="close" />,
        icon: null,
        okText: t("confirm"),
        cancelText: t("cancel"),
        content: t("cfd.nonTradingHoursOpenConfirm"),
        onOk: proceedToOrderConfirmOrSubmit,
      });
    } else {
      proceedToOrderConfirmOrSubmit();
    }
  }, [
    loading,
    isLogin,
    inputValue,
    orderInputType,
    availableBalance,
    margin,
    tradeType,
    price,
    tradeSide,
    leverage,
    usedPrice,
    name,
    currentPrice,
    calcMarginResult,
    calcQuantityResult,
    cfdConfig.marginPrecision,
    coinQuantityPrecisionMarket,
    coinPricePrecisionMarket,
    coinQuantityUpperCase,
    coinPriceUpperCase,
    t,
  ]);

  // 打开底部弹窗：未登录跳转登录页，已登录选择「充值法币」或「闪兑」
  const handleAddClick = useCallback(() => {
    if (!isLogin) {
      location.href = getUrl("/accounts/login?backurl=" + encodeURIComponent(typeof location !== "undefined" ? location.href : ""));
      return;
    }
    setChoiceSheetOpen(true);
  }, [isLogin]);

  const isFiatContract = useMemo(() => {
    const quoteCurrency = name ? name.split("_")[1]?.toUpperCase() : "USDT";
    return isFiatMarketCode(quoteCurrency);
  }, [name]);

  const handleChooseDeposit = useCallback(() => {
    setDepositDrawerOpen(true);
  }, []);

  // 余额不足弹窗：点击充值跳转到对应法币/加密货币充值页
  const handleInsufficientBalanceDeposit = useCallback(() => {
    setInsufficientBalanceModalOpen(false);
    const quoteCurrency = name ? name.split("_")[1]?.toUpperCase() : "USDT";
    const isFiat = isFiatMarketCode(quoteCurrency);
    if (isFiat) {
      location.href = getUrl("/wallet/account/common/deposit/fiat");
    } else {
      location.href = getUrl("/wallet/account/common/deposit?currency=usdt");
    }
  }, [name]);

  return (
    <div id="cfd-trade-form-pro" className={styles.tradeFormPro}>
      {/* 杠杆选择 */}
      <div className={styles.leverageRow} data-guide-target="proLeverageRow">
        <LeverageSelector
          value={leverage}
          min={cfdConfig.leverageRange?.min || 1}
          max={cfdConfig.leverageRange?.max || 200}
          onChange={setLeverage}
        />
      </div>

      {/* 交易类型选择 */}
      <div className={styles.tradeTypeRow} data-guide-target="proTradeTypeRow">
        <Dropdown menu={{ items: tradeTypeDropdownItems }} trigger={["click"]} overlayClassName={styles.tradeTypeDropdown}>
          <div className={styles.tradeTypeSelect}>
            <span>{tradeType === TradeTypeEnum.limit ? t("cfd.limitOrder") : t("cfd.marketOrder")}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none">
              <path d="M10 0H0L5 6L10 0Z" fill="rgba(255, 255, 255, 0.56)" />
            </svg>
          </div>
        </Dropdown>
      </div>

      {/* 市价单：市场最优价 */}
      {tradeType === TradeTypeEnum.market && (
        <div className={styles.marketBestPriceRow} data-guide-target="proPriceInput">
          <input
            type="text"
            className={styles.marketBestPriceInput}
            value={t("cfd.marketBestPrice")}
            disabled={true}
          />
        </div>
      )}

      {/* 限价单：价格输入 */}
      {tradeType === TradeTypeEnum.limit && (
        <div className={styles.priceRow} data-guide-target="proPriceInput">
          <AppInputNumber
            className={styles.priceInput}
            classInput={styles.priceInputInner}
            value={price}
            onInput={handleInputPrice}
            placeholder={t("cfd.pleaseEnterPrice")}
            point={coinPricePrecisionMarket}
            isStepPoint={true}
          />
          <span className={styles.currencyLabel}>{coinPriceUpperCase}</span>
        </div>
      )}

      {/* 市价单/限价单：数量/开仓价值/保证金输入 */}
      <div className={styles.quantityRow} data-guide-target="proQuantityRow">
        <div className={styles.quantityInputWrapper}>
          {/* 减号按钮 */}
          <button
            className={styles.quantityButton}
            onClick={() => {
              if (quantityInputRef.current && quantityInputRef.current.decrease) {
                quantityInputRef.current.decrease();
              }
            }}
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4 8H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          
          {/* 输入框 */}
          <div className={styles.quantityInputContainer}>
            <XtInputNumber
              ref={quantityInputRef}
              className={styles.quantityInput}
              value={inputValue}
              onInput={handleInputValue}
              placeholder={t("cfd.pleaseEnterQuantity")}
              point={
                orderInputType === OrderInputTypeEnum.quantity
                  ? coinQuantityPrecisionMarket
                  : coinPricePrecisionMarket
              }
              isStepPoint={true}
              max={orderInputType === OrderInputTypeEnum.margin ? (Number(availableBalance) || Infinity) : Infinity}
            />
          </div>
          
          {/* 加号按钮 */}
          <button
            className={styles.quantityButton}
            onClick={() => {
              if (orderInputType === OrderInputTypeEnum.margin) {
                const availableBalanceNum = Big(availableBalance || 0);
                const currentInput = Big(inputValue || 0);
                if (availableBalanceNum.lte(0) || currentInput.gte(availableBalanceNum)) {
                  setInsufficientBalanceModalOpen(true);
                  return;
                }
              }
              if (quantityInputRef.current && quantityInputRef.current.increase) {
                quantityInputRef.current.increase();
              }
            }}
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 4V12M4 8H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        <Dropdown
          menu={{ items: orderInputTypeDropdownItems }}
          trigger={["click"]}
          overlayClassName={styles.leverageDropdown}
        >
          <div className={styles.orderTypeSelect}>
            <span>
              {orderInputType === OrderInputTypeEnum.quantity
                ? coinQuantityUpperCase
                : orderInputType === OrderInputTypeEnum.value
                ? t("cfd.value")
                : t("cfd.margin")}
            </span>
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="6" viewBox="0 0 10 6" fill="none">
              <path d="M10 0H0L5 6L10 0Z" fill="rgba(255, 255, 255, 0.56)" />
            </svg>
          </div>
        </Dropdown>
      </div>

      {/* 可用余额和添加按钮 */}
      <div className={styles.availableRow}>
        <span>
          Avbl: {availableBalanceLabel} {availableBalanceDisplayUnit ?? coinPriceUpperCase}
        </span>
        <button type="button" className={styles.addBtn} data-guide-target="cfdStandardGuideDeposit" onClick={handleAddClick}>
          <img src={addIcon} alt="add" />
        </button>
      </div>

      {/* 数量滑块 */}
      <div className={styles.sliderWrapper}>
        <Slider
          className={styles.slider}
          value={sliderTotalMarginValue}
          max={availableBalance || "0"}
          point={coinPricePrecisionMarket}
          disabled={false}
          onChange={handleSliderChange}
          onDragStateChange={setIsSliderDragging}
          onAfterChange={() => setIsSliderDragging(false)}
        />
      </div>

      {/* TP/SL 复选框 */}
      <div className={styles.tpSlRow}>
        <Checkbox
          checked={tpSlEnabled}
          onChange={(e) => {
            const checked = e.target.checked;
            setTpSlEnabled(checked);
            if (!checked) {
              // 取消勾选时清空设置
              setTakeProfitPrice("");
              setStopLossPrice("");
            }
          }}
        />
        <span className={styles.tpSlLabel}>{t("trade.tp2sl") || "TP/SL"}</span>
      </div>

      {/* TP/SL 输入框 */}
      {tpSlEnabled && (
        <div className={styles.tpSlInputs}>
          <div className={styles.tpSlInputRow}>
            <label className={styles.tpSlInputLabel}>{t("cfd.stopProfit")}</label>
            <input
              className={styles.tpSlInput}
              type="text"
              placeholder={t("cfd.pleaseEnterPrice")}
              value={takeProfitPrice}
              onChange={handleTakeProfitInput}
            />
          </div>
          <div className={styles.tpSlInputRow}>
            <label className={styles.tpSlInputLabel}>{t("cfd.stopLoss")}</label>
            <input
              className={styles.tpSlInput}
              type="text"
              placeholder={t("cfd.pleaseEnterPrice")}
              value={stopLossPrice}
              onChange={handleStopLossInput}
            />
          </div>
        </div>
      )}

      {/* 最多下单量 */}
      {/* <div className={styles.maxOrderRow}>
        <span>{t("cfd.maxOrderQuantity")}: {maxQuantity} {coinQuantityUpperCase}</span>
      </div> */}

      {/* 数量、保证金和开仓价值 */}
      {/* <div className={styles.infoRow}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>{t("cfd.quantity")}</span>
          <span className={styles.infoValue}>
            {quantity} {coinQuantityUpperCase}
          </span>
        </div>

        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>{t("cfd.openValue")}</span>
          <span className={styles.infoValue}>
            {value} {coinPriceUpperCase}
          </span>
        </div>
        {orderInputType !== OrderInputTypeEnum.margin && (
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t("cfd.margin")}</span>
            <span className={styles.infoValue}>
              {margin} {coinPriceUpperCase}
            </span>
          </div>
        )}
      </div> */}

      {/* 止盈/止损 */}
      {/* <div className={styles.stopLossRow}>
        <span>{t("cfd.takeProfitStopLoss")}：{tpSlRangeText}</span>
      </div> */}

      {/* 买涨/买跌按钮 */}
      <div className={styles.actionButtons} data-guide-target="proActionButtons">
        {isLogin ? (
          <>
            {/* 买涨按钮区域 */}
            <div className={styles.actionButtonGroup}>
              <div className={styles.actionInfoRow}>
                <div className={styles.actionInfoItem}>
                  <span className={styles.actionInfoLabel}>{t("cfd.maxOpen")}</span>
                  <span className={styles.actionInfoValue}>{maxOpenBuy} {coinPriceUpperCase}</span>
                </div>
                <div className={styles.actionInfoItem}>
                  <span className={styles.actionInfoLabel}>{t("cfd.cost")}</span>
                  <span className={styles.actionInfoValue}>{costBuy} {coinPriceUpperCase}</span>
                </div>
              </div>
              <button
                type="button"
                className={cx(
                  styles.actionBtn,
                  klineColorScheme === "green_up" ? styles.buyBtn : styles.sellBtn,
                  { [styles.active]: tradeSide === TradeSideEnum.buy }
                )}
                style={
                  klineColorScheme === "red_up"
                    ? {
                        background: "#d94f4f",
                      }
                    : undefined
                }
                onClick={() => {
                  setTradeSide(TradeSideEnum.buy);
                  handleSubmit();
                }}
                disabled={loading}
              >
                {loading ? t("cfd.submitting") : t("cfd.buyUp")}
              </button>
            </div>
            {/* 买跌按钮区域 */}
            <div className={styles.actionButtonGroup}>
              <div className={styles.actionInfoRow}>
                <div className={styles.actionInfoItem}>
                  <span className={styles.actionInfoLabel}>{t("cfd.maxOpen")}</span>
                  <span className={styles.actionInfoValue}>{maxOpenSell} {coinPriceUpperCase}</span>
                </div>
                <div className={styles.actionInfoItem}>
                  <span className={styles.actionInfoLabel}>{t("cfd.cost")}</span>
                  <span className={styles.actionInfoValue}>{costSell} {coinPriceUpperCase}</span>
                </div>
              </div>
              <button
                type="button"
                className={cx(
                  styles.actionBtn,
                  klineColorScheme === "green_up" ? styles.sellBtn : styles.buyBtn,
                  { [styles.active]: tradeSide === TradeSideEnum.sell }
                )}
                style={
                  klineColorScheme === "red_up"
                    ? {
                        background: "#45bf59",
                      }
                    : undefined
                }
                onClick={() => {
                  setTradeSide(TradeSideEnum.sell);
                  handleSubmit();
                }}
                disabled={loading}
              >
                {loading ? t("cfd.submitting") : t("cfd.buyDown")}
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            className={cx(styles.actionBtn, styles.loginBtn)}
            onClick={() => {
              const query = "?backurl=" + encodeURIComponent(location.href);
              location.href = getUrl("/accounts/login" + query);
            }}
          >
            {t("trade.login2Register")}
          </button>
        )}
      </div>

      {/* 余额不足弹窗 */}
      <Modal
        className={styles.insufficientBalanceModal}
        open={insufficientBalanceModalOpen}
        onCancel={() => setInsufficientBalanceModalOpen(false)}
        footer={null}
        centered
        closable={false}
        width={320}
        maskClosable={true}
      >
        <div className={styles.insufficientBalanceContent}>
          <div className={styles.insufficientBalanceTitle}>{t("cfd.tip")}</div>
          <div className={styles.insufficientBalanceDesc}>{t("cfd.insufficientBalanceDesc")}</div>
          <div className={styles.insufficientBalanceActions}>
            <button className={styles.insufficientBalanceCancel} onClick={() => setInsufficientBalanceModalOpen(false)}>
              {t("cancel")}
            </button>
            <button className={styles.insufficientBalanceDeposit} onClick={handleInsufficientBalanceDeposit}>
              {t("cfd.deposit")}
            </button>
          </div>
        </div>
      </Modal>
      <DepositOrConvertDrawer
        open={choiceSheetOpen}
        onClose={() => setChoiceSheetOpen(false)}
        isFiatContract={isFiatContract}
        onChooseDeposit={handleChooseDeposit}
      />
      <DepositDrawer 
        open={depositDrawerOpen} 
        onClose={() => setDepositDrawerOpen(false)}
        currency={currency}
      />

    </div>
  );
};

export default observer(TradeFormPro);
